import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HiOutlineChevronRight, HiOutlineMenu } from "react-icons/hi";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  getSurveillanceDiscussionDetails,
  skipSurveillanceStep,
  SurveillanceWorkflowStep,
} from "api/surveillance";
import InactiveStepView from "./InactiveStepView";
import SurveillancePreliminaryDiscussionView, {
  SURVEILLANCE_PRELIMINARY_FORM_ID,
} from "./SurveillancePreliminaryDiscussionView";
import SurveillanceApplicationDetailsFromClientView, {
  SURVEILLANCE_APPLICATION_DETAILS_FORM_ID,
} from "./SurveillanceApplicationDetailsFromClientView";
import SurveillanceInvoiceToClientView, {
  SURVEILLANCE_INVOICE_TO_CLIENT_FORM_ID,
} from "./SurveillanceInvoiceToClientView";
import SurveillancePaymentFromClientView, {
  SURVEILLANCE_PAYMENT_FROM_CLIENT_FORM_ID,
} from "./SurveillancePaymentFromClientView";
import SurveillanceDetailedSearchView, {
  SURVEILLANCE_DETAILED_SEARCH_FORM_ID,
} from "./SurveillanceDetailedSearchView";
import SurveillanceNoteOfSurveillanceView, {
  SURVEILLANCE_NOTE_OF_SURVEILLANCE_FORM_ID,
} from "./SurveillanceNoteOfSurveillanceView";

type Section = "discussion" | "status";

const DISCUSSION_ITEMS: { id: string; label: string }[] = [
  {
    id: "preliminary",
    label: "Preliminary Discussion with the Client for Surveillance",
  },
  { id: "application-details", label: "Application details from Client" },
  { id: "invoice", label: "Invoice to Client" },
  { id: "payment", label: "Payment from Client for Surveillance" },
];

const DISCUSSION_ID_TO_SLUG: Record<string, string> = {
  preliminary: "preliminary-discussion-with-the-client",
  "application-details": "application-details-from-client",
  invoice: "invoice-to-client",
  payment: "payment-from-client",
};

const DISCUSSION_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(DISCUSSION_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const STATUS_ITEMS: { id: string; label: string }[] = [
  { id: "detailed-search", label: "Detailed Search" },
  { id: "note-of-surveillance", label: "Note of Surveillance" },
];

const STATUS_ID_TO_SLUG: Record<string, string> = {
  "detailed-search": "detailed-search",
  "note-of-surveillance": "note-of-surveillance",
};

const STATUS_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(STATUS_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const FORM_MAP: Record<string, string | undefined> = {
  preliminary: SURVEILLANCE_PRELIMINARY_FORM_ID,
  "application-details": SURVEILLANCE_APPLICATION_DETAILS_FORM_ID,
  invoice: SURVEILLANCE_INVOICE_TO_CLIENT_FORM_ID,
  payment: SURVEILLANCE_PAYMENT_FROM_CLIENT_FORM_ID,
  "detailed-search": SURVEILLANCE_DETAILED_SEARCH_FORM_ID,
  "note-of-surveillance": SURVEILLANCE_NOTE_OF_SURVEILLANCE_FORM_ID,
};

export default function DashboardSurveillance() {
  const navigate = useNavigate();
  const { section, step, leadId } = useParams<{
    section?: string;
    step?: string;
    leadId?: string;
  }>();

  const [menuOpen, setMenuOpen] = useState(false);
  const [markNameForHeader, setMarkNameForHeader] = useState<string | null>(null);
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [progressing, setProgressing] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setMarkNameForHeader(null);
      return;
    }
    let cancelled = false;
    getSurveillanceDiscussionDetails(leadId)
      .then((details) => {
        if (!cancelled)
          setMarkNameForHeader(details.preliminary.markName ?? null);
      })
      .catch(() => {
        if (!cancelled) setMarkNameForHeader(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeTab: Section = section === "status" ? "status" : "discussion";

  const activeMenuItem =
    activeTab === "discussion" && step
      ? (DISCUSSION_SLUG_TO_ID[step] ?? "preliminary")
      : activeTab === "status" && step
        ? (STATUS_SLUG_TO_ID[step] ?? "detailed-search")
        : activeTab === "status"
          ? "detailed-search"
          : "preliminary";

  useEffect(() => {
    if (!section || !step) {
      navigate(
        "/dashboard/surveillance/new/discussion/preliminary-discussion-with-the-client",
        { replace: true }
      );
    }
  }, [section, step, navigate]);

  const navigateToStep = (
    tab: Section,
    itemId: string,
    currentLeadId?: string | null
  ) => {
    const slug =
      tab === "discussion"
        ? DISCUSSION_ID_TO_SLUG[itemId]
        : STATUS_ID_TO_SLUG[itemId];
    if (!slug) return;
    const base = `/dashboard/surveillance/new/${tab}/${slug}`;
    const path = currentLeadId ? `${base}/${currentLeadId}` : base;
    navigate(path);
    setMenuOpen(false);
  };

  const handleTabChange = (tab: Section) => {
    const firstStep = tab === "status" ? "detailed-search" : "preliminary";
    const slug =
      tab === "status"
        ? STATUS_ID_TO_SLUG[firstStep]
        : DISCUSSION_ID_TO_SLUG[firstStep];
    const path = leadId
      ? `/dashboard/surveillance/new/${tab}/${slug}/${leadId}`
      : `/dashboard/surveillance/new/${tab}/${slug}`;
    navigate(path);
  };

  const leftPanelItems =
    activeTab === "status" ? STATUS_ITEMS : DISCUSSION_ITEMS;

  const activeLeftId = activeMenuItem;
  const activeIdx = leftPanelItems.findIndex((m) => m.id === activeLeftId);
  const canGoBack = activeIdx > 0;

  const submitForm = FORM_MAP[activeMenuItem] ?? null;

  const isInvoiceStep = activeTab === "discussion" && activeMenuItem === "invoice";
  const primaryActionLabel =
    isInvoiceStep && !invoiceSent ? "Send & Continue" : "Save & Continue";

  // Progress (skip) button: shown on the Payment step.
  const progressStep =
    activeTab === "discussion" && activeMenuItem === "payment"
      ? { step: SurveillanceWorkflowStep.SV_S1_04_PaymentFromClient }
      : null;

  const handleProgress = async () => {
    if (!progressStep || !leadId || progressing) return;
    setProgressing(true);
    try {
      await skipSurveillanceStep(leadId, progressStep.step);
      toast.success("Step marked as skipped.");
      navigateToStep("status", "detailed-search", leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to skip step");
    } finally {
      setProgressing(false);
    }
  };

  return (
    <DashboardLayout
      headerTitle="Surveillance"
      headerLeadNo={markNameForHeader ?? undefined}
      hideHeaderOnMobile
    >
      <div className="flex-1 p-2 min-h-0 flex flex-col min-w-0">
        <div className="bg-white rounded-[8px] h-full flex flex-col overflow-hidden">
          {/* Tab bar */}
          <div className="flex items-center gap-0 border-b border-[#EEEEEE] pl-[10px] pt-[6px] flex-shrink-0">
            {(["discussion", "status"] as Section[]).map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-2.5 py-2.5 text-[14px] leading-5 -mb-px border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? "font-semibold text-[#222] border-[#222]"
                    : "font-normal text-[#222] border-transparent"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Mobile header */}
          <div className="md:hidden flex items-center gap-3 h-[65px] px-4 border-b border-[#E5E7EB] flex-shrink-0">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              className="flex items-center justify-center rounded-[10px] w-9 h-9 min-w-[36px] min-h-[36px] hover:bg-[#F7F7F7]"
            >
              <HiOutlineMenu className="w-5 h-5 text-[#222]" strokeWidth={1.5} />
            </button>
            <div className="flex-1 min-w-0 font-medium text-[14px] leading-5 text-[#242424] truncate">
              Surveillance
              {markNameForHeader ? ` | ${markNameForHeader}` : ""}
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden relative">
            {/* Mobile overlay */}
            {menuOpen && (
              <div
                className="absolute inset-0 bg-black/20 z-40 md:hidden"
                onClick={() => setMenuOpen(false)}
              />
            )}

            {/* Left panel */}
            <div
              className={`w-[279px] border-r border-[#EEEEEE] bg-white p-2.5 overflow-y-auto flex-shrink-0 ${
                menuOpen
                  ? "absolute left-0 top-0 bottom-0 z-50 md:relative"
                  : "hidden md:block"
              }`}
            >
              <div className="flex flex-col gap-2">
                {leftPanelItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() =>
                      navigateToStep(activeTab, item.id, leadId ?? null)
                    }
                    className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs ${
                      activeLeftId === item.id
                        ? "bg-[#222222] text-white font-medium"
                        : "bg-[#F7F7F7] text-[#222222] font-normal"
                    } hover:bg-opacity-90 transition-colors`}
                  >
                    <span className="text-left line-clamp-1">{item.label}</span>
                    <HiOutlineChevronRight
                      className="w-4 h-4 flex-shrink-0 ml-1"
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex-1 p-5 md:p-6 overflow-y-auto">
                {activeTab === "discussion" && activeMenuItem === "preliminary" ? (
                  <SurveillancePreliminaryDiscussionView
                    leadId={leadId ?? undefined}
                    onSuccess={(newLeadId) => {
                      navigateToStep(
                        "discussion",
                        "application-details",
                        newLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "application-details" ? (
                  <SurveillanceApplicationDetailsFromClientView
                    leadId={leadId ?? undefined}
                    onSuccess={(newLeadId) => {
                      navigateToStep("discussion", "invoice", newLeadId);
                    }}
                  />
                ) : activeTab === "discussion" && activeMenuItem === "invoice" ? (
                  <SurveillanceInvoiceToClientView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInvoiceSent}
                    onSuccess={(newLeadId) => {
                      navigateToStep("discussion", "payment", newLeadId);
                    }}
                  />
                ) : activeTab === "discussion" && activeMenuItem === "payment" ? (
                  <SurveillancePaymentFromClientView
                    leadId={leadId ?? null}
                    onSuccess={(newLeadId) => {
                      navigateToStep("status", "detailed-search", newLeadId);
                    }}
                  />
                ) : activeTab === "status" &&
                  activeMenuItem === "detailed-search" ? (
                  <SurveillanceDetailedSearchView
                    leadId={leadId ?? undefined}
                    onSuccess={(newLeadId) => {
                      navigateToStep("status", "note-of-surveillance", newLeadId);
                    }}
                  />
                ) : activeTab === "status" &&
                  activeMenuItem === "note-of-surveillance" ? (
                  <SurveillanceNoteOfSurveillanceView
                    leadId={leadId ?? undefined}
                    onSuccess={(newLeadId) => {
                      navigateToStep("status", "note-of-surveillance", newLeadId);
                    }}
                  />
                ) : (
                  <InactiveStepView
                    title="Step coming soon"
                    body="This step has not been implemented yet. Please continue with the available steps."
                    buttonText="Back to Preliminary Discussion"
                    onButtonClick={() =>
                      navigateToStep("discussion", "preliminary", leadId ?? null)
                    }
                  />
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-[#EEE] p-3 flex items-center justify-between gap-2 flex-shrink-0">
                <button
                  type="button"
                  disabled={!canGoBack}
                  onClick={() => {
                    if (!canGoBack) return;
                    navigateToStep(
                      activeTab,
                      leftPanelItems[activeIdx - 1].id,
                      leadId ?? null
                    );
                  }}
                  className="px-4 py-2 h-9 text-[12px] font-semibold text-[#6D6D6D] border border-[#EEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <div className="flex items-center gap-2 sm:gap-3 md:gap-5">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/surveillance")}
                    className="px-4 py-2 h-9 text-[12px] font-semibold text-[#6D6D6D] border border-[#EEE] rounded-[6px] bg-white hover:bg-[#F7F7F7]"
                  >
                    Close
                  </button>
                  {progressStep && (
                    <button
                      type="button"
                      onClick={handleProgress}
                      disabled={progressing}
                      className="px-4 py-2 h-9 text-[12px] font-semibold text-[#222] border border-[#222] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {progressing ? "Progressing…" : "Progress"}
                    </button>
                  )}
                  <button
                    type={submitForm ? "submit" : "button"}
                    form={submitForm ?? undefined}
                    disabled={!submitForm}
                    className="px-4 py-2 h-9 min-w-[128px] text-[12px] font-semibold text-white rounded-[6px] bg-[#222] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {primaryActionLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
